import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

export interface LazyListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  itemHeight?: number;
  containerHeight?: number;
  overscan?: number;
  loading?: boolean;
  loadingComponent?: React.ReactNode;
  emptyComponent?: React.ReactNode;
  className?: string;
  onScroll?: (scrollTop: number) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  threshold?: number;
}

export function LazyList<T>({
  items,
  renderItem,
  itemHeight = 50,
  containerHeight = 400,
  overscan = 5,
  loading = false,
  loadingComponent,
  emptyComponent,
  className = '',
  onScroll,
  onLoadMore,
  hasMore = false,
  threshold = 100
}: LazyListProps<T>) {
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = useRef(false);

  const visibleRange = useMemo(() => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(
      items.length - 1,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
    );
    return { startIndex, endIndex };
  }, [scrollTop, itemHeight, containerHeight, overscan, items.length]);

  const visibleItems = useMemo(() => {
    return items.slice(visibleRange.startIndex, visibleRange.endIndex + 1);
  }, [items, visibleRange]);

  const totalHeight = items.length * itemHeight;
  const offsetY = visibleRange.startIndex * itemHeight;

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const newScrollTop = event.currentTarget.scrollTop;
    setScrollTop(newScrollTop);
    onScroll?.(newScrollTop);

    // Check if we need to load more items
    if (
      hasMore &&
      onLoadMore &&
      !isLoadingMore.current &&
      newScrollTop + containerHeight >= totalHeight - threshold
    ) {
      isLoadingMore.current = true;
      onLoadMore();
    }
  }, [onScroll, hasMore, onLoadMore, containerHeight, totalHeight, threshold]);

  // Reset loading flag when items change
  useEffect(() => {
    isLoadingMore.current = false;
  }, [items.length]);

  if (items.length === 0 && !loading) {
    return (
      <div className={`relative overflow-y-auto overflow-x-hidden flex items-center justify-center min-h-[200px] ${className}`}>
        {emptyComponent || <div className="text-slate-500 dark:text-slate-400 italic text-center p-8">No items to display</div>}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-slate-100 dark:scrollbar-track-slate-800 scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 hover:scrollbar-thumb-slate-400 dark:hover:scrollbar-thumb-slate-500 ${className}`}
      style={{ height: containerHeight, overflow: 'auto' }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div
          style={{
            transform: `translateY(${offsetY}px)`,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
          }}
        >
          {visibleItems.map((item, index) => (
            <div
              key={visibleRange.startIndex + index}
              style={{ height: itemHeight }}
              className="flex items-center border-b border-slate-200 dark:border-slate-700 transition-colors duration-200 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              {renderItem(item, visibleRange.startIndex + index)}
            </div>
          ))}
        </div>
      </div>
      {loading && (
        <div className="sticky bottom-0 bg-white/90 dark:bg-slate-900/90 p-4 text-center border-t border-slate-200 dark:border-slate-700">
          {loadingComponent || (
            <div className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <span className="w-4 h-4 border-2 border-slate-200 dark:border-slate-600 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></span>
              Loading...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
